import React from "react";
import { Col, Divider, Row, Tag, Alert } from "antd";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "components/MainLayout";

import useGet from "hooks/useGet";
import PageFailure from "components/PageFailure";
import Logs from "components/Logs";
import Comments from "components/Comments";
import IssueGallery from "components/IssueGallery";
import IssueFiles from "components/IssueFiles";
import IssueSignature from "components/IssueSignature";
import IssueWorker from "components/IssueWorker";
import IssueEditForm from "./components/Form";
import SubIssues from "./components/SubIssues";
import MasterIssue from "./components/MasterIssue";
import ConvertToMasterIssue from "./components/ConvertToMasterIssue";
import Statuses from "./components/Statuses";
import getIssueType from "helpers/issue_types";

const IssueUpdate = () => {
  const { id } = useParams();

  const path = `/issues/${id}`;

  const { is_loading, is_fetching, is_error, data, refetch } = useGet({
    path,
    query: ["issues", id],
    options: {
      refetchOnWindowFocus: false,
    },
  });

  if (is_error) {
    return (
      <PageFailure
        title="Nie znaleziono zgłoszenia"
        button_text="Wróć do listy"
        path="/issues"
      />
    );
  }

  const issue_type = getIssueType(data?.issue_type);

  return (
    <MainLayout
      back_href="/issues"
      breadcrumbs={[
        { name: "Zgłoszenia", path: "/issues" },
        { name: data?.id || "Trwa pobieranie..." },
      ]}
    >
      {data?.deleted_at && (
        <Alert
          type="error"
          banner
          message="UWAGA!!"
          showIcon
          description={`Zgłoszenie zostało usunięte dnia ${dayjs(
            data?.deleted_at
          ).format("DD-MM-YYYY HH:mm")}`}
        />
      )}
      <Statuses data={data} />
      {!is_loading && issue_type && (
        <Divider className="m-t-40 m-b-40">
          <Tag
            color={issue_type.color}
            style={{ fontSize: 16, padding: "8px 20px", lineHeight: "24px" }}
          >
            {issue_type.label}
          </Tag>
        </Divider>
      )}
      {data?.issue_group && (
        <SubIssues
          client_phone={data?.client_phone}
          issue_group_id={data?.issue_group?.id}
          issue_id={data?.id}
          sub_issues={data?.sub_issues}
          refetch={refetch}
        />
      )}
      <Row gutter={[24, 24]}>
        <Col xs={24} md={24} lg={14} xl={14}>
          {!data?.issue_group?.id && !data?.sub_issue_group?.id && (
            <ConvertToMasterIssue master_issue_id={id} refetch={refetch} />
          )}
          {data?.sub_issue_group && (
            <MasterIssue master_issue={data.sub_issue_group.master_issue} />
          )}
          <IssueEditForm
            key={id}
            data={data}
            is_loading={is_loading}
            is_fetching={is_fetching}
            refetch={refetch}
          />
        </Col>
        <Col xs={24} md={24} lg={10} xl={10}>
          <IssueWorker data={data} refetch={refetch} />
          <Comments
            issue_id={id}
            refetch={refetch}
            data={data?.issue_comments || []}
          />
          {!is_loading && (
            <>
              <IssueGallery issue_id={id} data={data?.issue_images || []} />
              <IssueFiles issue_id={id} data={data?.issue_files || []} />
            </>
          )}
          <IssueSignature data={data?.issue_signature} />
          <Logs
            title="Logi zgłoszenia"
            loading={is_loading}
            styles={{
              body: {
                maxHeight: 600,
                overflowY: "auto",
              },
            }}
            data={data?.logs || []}
          />
        </Col>
      </Row>
    </MainLayout>
  );
};

export default IssueUpdate;
